
import { useState, useRef } from "react"
import { motion } from "framer-motion"

// Add custom styles for the pulse animation
const pulseStyle = {
  animationDuration: "2s",
  animationIterationCount: "infinite",
}

interface VideoPlayerProps {
  videoId: string
  title?: string
  onPlayStateChange?: (isPlaying: boolean) => void
}

export function VideoPlayer({ videoId, title, onPlayStateChange }: VideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Update the handlePlay function to load the video immediately without the artificial delay
  const handlePlay = () => {
    // Set playing state immediately
    setIsPlaying(true)

    // Notify parent component that video is playing
    if (onPlayStateChange) {
      onPlayStateChange(true)
    }
  }

  // Remove the loading state and spinner code
  // Replace the entire return statement with this updated version:
  return (
    <div className="relative w-full h-full aspect-[16/9] rounded-2xl overflow-hidden">
      {!isPlaying ? (
        <motion.div
          className="absolute inset-0 z-10 cursor-pointer group"
          onClick={handlePlay}
          aria-label={`Play ${title} video`}
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1.0] }}
        >
          {/* Default YouTube thumbnail with hover effect */}
          <img
            src={`https://assets.avepoint.com/upload/images/landingpage/pricing/the-data-security-platform-for-the-public-sector.webp`}
            alt={title || "Video thumbnail"}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />

          {/* Play button overlay with no dark tint */}
          <div className="absolute inset-0 flex items-center justify-center transition-all duration-300">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white shadow-lg group-hover:scale-110 transition-transform duration-300 flex items-center justify-center relative">
              {/* Add pulsing ring animation */}
              <span
                className="absolute w-full h-full rounded-full bg-white/30 animate-ping opacity-75"
                style={pulseStyle}
              ></span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="#ef483d"
                className="w-8 h-8 sm:w-10 sm:h-10 ml-1 relative z-10"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </motion.div>
      ) : (
        <iframe
          ref={iframeRef}
          className="w-full h-full"
          src={`https://www.youtube.com/embed/${videoId}?rel=0&autoplay=1`}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      )}
    </div>
  )
}
